<template>
  <nav
    ref="target"
    :class="{
      'overflow-hidden md:h-full md:overflow-visible': true,
    }"
  >
    <button
      v-if="model"
      type="button"
      aria-label="Active Menu"
      :class="{
        'absolute top-25px text-www-color-white md:hidden': true,
        'flex items-center gap-5': true,
        'pl-10 pr-6 text-right': true,
      }"
      @click="model = null"
    >
      <a
        :class="{
          'back-arrow inline-block rotate-[135deg] border-b-[3px] border-r-[3px] border-www-color-white p-3px': true,
        }"
      ></a>
      <p
        :class="{
          'text-15px font-bold uppercase leading-none text-www-color-white': true,
        }"
      >
        {{ activeMenuTitle }}
      </p>
    </button>
    <!-- Divider in mobile view -->
    <div
      :class="{
        'divider ml-25px mr-25px border-b border-www-color-neutral-medium md:hidden': true,
      }"
    ></div>
    <ul
      :class="{
        'edu-list mx-py-0 flex h-full flex-col justify-between gap-4 md:flex-row md:items-center md:gap-0': true,
        'overflow-hidden px-50px pb-4 pt-25px md:overflow-visible md:px-0 md:py-0': true,
      }"
    >
      <template v-for="(eduNavLink, index) in menus">
        <li
          v-if="eduNavLink._type === 'generalLink'"
          :key="index"
          :class="{
            'list relative h-full': true,
          }"
        >
          <GeneralLinkComponent
            v-model="model"
            :index="index"
            v-bind="eduNavLink"
            :class="{
              'text-13px font-bold uppercase leading-none text-www-color-white md:text-10px md:font-medium lg:text-13px 2xl:text-18px': true,
              'whitespace-nowrap transition-all duration-300': true,
              'md:underline': isActive(index),
            }"
          />
        </li>
        <EducationDropdown
          v-else-if="eduNavLink._type === 'educationDropdown'"
          :key="eduNavLink._key + 'eduDropdown'"
          v-model="model"
          :active-menu="eduNavLink._key"
          v-bind="eduNavLink"
          :class="{
            'h-full md:relative': true,
          }"
        />
      </template>
    </ul>
  </nav>
</template>

<script lang="ts" setup>
import type {
  EducationDropdown as EducationDropdownType,
  EducationHeader,
  GeneralLink,
} from '~/sanity/types';
import { onClickOutside } from '@vueuse/core';
import GeneralLinkComponent from '~/components/Molecules/HeaderComponents/GeneralLink.vue';
import EducationDropdown from './EduDropdown.vue';
interface ExtendedEducationDropdown extends EducationDropdownType {
  _key: string;
}
const props = defineProps<EducationHeader>();
const model = defineModel<string | null>({ default: null });

// Click outside to close the dropdown
const target = ref(null);
onClickOutside(target, () => {
  model.value = null;
});
// Get Active menu title to display in mobile menu header
const dropdownItems = props.menus?.filter(
  (item) => item._type === 'educationDropdown'
) as ExtendedEducationDropdown[];
const activeMenuTitle = computed(() => {
  return dropdownItems?.find((item) => item._key === model.value)?.title || '';
});
async function resolveUrl(link: GeneralLink): Promise<string> {
  const linkItem = link.link?.[0];
  if (!linkItem) return '#';
  if (linkItem._type === 'internalLink') {
    const { getResolvedInternalLinkRef } = useRefsStore();
    const resolved = await getResolvedInternalLinkRef(linkItem.selectedPage?.reference?._ref);
    return resolved?.value?.currentPathname || '#';
  }
  if (linkItem._type === 'externalLink') return linkItem.url || '#';
  return '#';
}

const route = useRoute();

const resolvedUrls = ref<Record<number, string>>({});

onMounted(async () => {
  if (!props.menus) return;
  for (let i = 0; i < props.menus.length; i++) {
    const item = props.menus[i];
    if (item?._type === 'generalLink') {
      const url = await resolveUrl(item as GeneralLink);
      resolvedUrls.value[i] = url || '#';
    }
  }
});

const isActive = (index: number) => {
  const url = resolvedUrls.value[index];
  if (!url || url === '#') return false;
  return route.path === url || route.path === url + '/';
};
</script>

<style lang="scss">
.edu-menu {
  .edu-list {
    .list {
      &:nth-child(1) {
        span {
          font-weight: bold !important;
        }
      }
    }
  }
}
</style>
